// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as authService from '../services/authService';
import { removeUserData, setUserData } from '../services/tokenStorage';
import { ApiError } from '../services/api';
import { getCurrentUser, USER_NOT_RESOLVED } from '../services/userService';
import { listSites } from '../services/omsService';
import { displaySiteLabel } from '../config/siteDisplayOverrides';
import { ME_STALE_TIME_MS } from '../config/queryConfig';
import { useSessionGuardStore } from '../stores/sessionGuardStore';
import type { UserData, MeResponse, UserRole, SiteSummary, SiteDetail } from '../types';

/** Project an OMS site onto the flat SiteSummary shape the shell consumes. */
const toSiteSummary = (s: SiteDetail): SiteSummary => ({
  site_id: s.siteId,
  site_key: s.siteKey,
  org_id: s.orgId,
  org_key: s.orgKey,
  label: displaySiteLabel(s.siteKey, s.label),
  region_id: s.regionId,
  region_key: s.regionKey,
  timezone: s.timezone,
  address: s.address,
  latitude: s.latitude,
  longitude: s.longitude,
});

const NO_SITES: SiteSummary[] = [];

/**
 * A /me 401 USER_NOT_RESOLVED (IAM has no user for the token, e.g. "User
 * missing tenantId") or 403 (no access) is the server's answer about this
 * user, so the cached profile must go. Any other 401 is a rejected token:
 * api.ts locks the session, as on every other route. Network errors and 5xx
 * keep the profile too.
 */
const refusal = (error: unknown): 401 | 403 | null => {
  if (!(error instanceof ApiError)) return null;
  if (error.status === 401 && error.code === USER_NOT_RESOLVED) return 401;
  return error.status === 403 ? 403 : null;
};

export interface AuthContextValue {
  user: UserData | null;
  meResponse: MeResponse | null;
  effectiveRole: UserRole | null;
  permissions: string[];
  sites: SiteSummary[];
  /** The organization the user's profile resolved to, or null before/without one. */
  orgId: string | null;
  isLoading: boolean;
  /** True when the OMS site fetch failed (distinct from "loaded, zero sites"). */
  sitesError: boolean;
  /** True when /me answered 403: the user is signed in but may not use the app. */
  noAccess: boolean;
  isAdmin: boolean;
  refetchMe: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Resolves the current user and their sites.
 *
 * Two dependent queries (ADR-0008): `['me']` gives the identity, then
 * `['oms', 'sites', orgId]` — gated on the organization the profile resolved to —
 * gives the site switcher's options. No sites → the shell shows an empty state;
 * the app never falls back to mock/static sites.
 *
 * The roster comes from OMS, not `/me`'s own `sites`: those are filtered by
 * SITE-scoped IAM assignments, which IAM does not issue yet.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  // MsalProvider reports no account until its first effect runs, so on a
  // reload the very first commit looks signed out. Reporting "loaded, no
  // sites" there let `useActiveSite` wipe the persisted site (AB#30702).
  const starting = useMsal().inProgress === InteractionStatus.Startup;
  const queryClient = useQueryClient();

  const [cachedUser] = useState<UserData | null>(() => authService.getUser());

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: async ({ signal }) => {
      let me: MeResponse;
      try {
        me = await getCurrentUser({ signal });
      } catch (error) {
        // One retry before signing out, in case IAM or the BFF answered 401
        // during a blip. MSAL renews tokens before they expire, so the retry
        // usually resends the same token.
        if (refusal(error) !== 401) throw error;
        me = await getCurrentUser({ signal });
      }
      setUserData(authService.meToUserData(me));
      return me;
    },
    enabled: isAuthenticated,
    // So role grants and revocations show without a reload (AB#35973). Only
    // once stale, and /me goes stale slowly, so tab switching doesn't hammer
    // IAM.
    refetchOnWindowFocus: true,
    staleTime: ME_STALE_TIME_MS,
  });

  const refused = refusal(meQuery.error);
  // react-query keeps the last good data next to a later error.
  const liveMe = refused ? undefined : meQuery.data;
  const orgId = liveMe?.organization_id;
  const meStatus = liveMe?.status;

  // /me is token-bound, so a deactivated account answers 200 with status
  // "inactive" rather than the 403 ACCOUNT_DISABLED the api layer locks on.
  useEffect(() => {
    if (meStatus === 'inactive') useSessionGuardStore.getState().lock('account_disabled');
  }, [meStatus]);

  const sitesQuery = useQuery({
    queryKey: ['oms', 'sites', orgId],
    queryFn: async ({ signal }) => {
      const omsSites = await listSites(orgId as string, undefined, { signal });
      return omsSites.map(toSiteSummary);
    },
    enabled: !!orgId,
  });

  useEffect(() => {
    if (meQuery.error) {
      console.error('[auth] Failed to resolve the current user —', meQuery.error);
    }
  }, [meQuery.error]);

  // Keyed on errorUpdatedAt too, so a later /me 401 (e.g. Retry) tries the
  // sign-out again if the first one failed, say while another MSAL
  // interaction was in progress.
  useEffect(() => {
    if (!refused) return;
    if (refused === 401) {
      // logout() clears the cached profile itself before redirecting.
      authService.logout().catch(error => console.error('[auth] Sign-out failed —', error));
    } else {
      removeUserData();
    }
  }, [refused, meQuery.errorUpdatedAt]);

  useEffect(() => {
    if (sitesQuery.error) {
      console.error('[OMS] Failed to load sites for the organization —', sitesQuery.error);
    }
  }, [sitesQuery.error]);

  const refetchMe = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['me'] }),
      queryClient.invalidateQueries({ queryKey: ['oms', 'sites'] }),
    ]);
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(() => {
    const meResponse = liveMe ?? null;
    const user = meResponse ? authService.meToUserData(meResponse) : refused ? null : cachedUser;
    const effectiveRole = meResponse
      ? authService.deriveEffectiveRole(meResponse)
      : (user?.default_role ?? null);
    const permissions = user?.effective_permissions ?? meResponse?.permissions ?? [];

    return {
      user,
      meResponse,
      effectiveRole,
      permissions,
      sites: sitesQuery.data ?? NO_SITES,
      orgId: orgId ?? null,
      isLoading:
        starting || (isAuthenticated && (meQuery.isPending || (!!orgId && sitesQuery.isPending))),
      // A failed background refetch (e.g. on window focus) keeps the last
      // good /me; only a /me that never loaded, or was refused, is an error.
      sitesError: (meQuery.isError && !liveMe) || sitesQuery.isError,
      noAccess: refused === 403,
      isAdmin: effectiveRole === 'admin',
      refetchMe,
    };
  }, [
    cachedUser,
    starting,
    isAuthenticated,
    orgId,
    refused,
    liveMe,
    meQuery.isPending,
    meQuery.isError,
    sitesQuery.data,
    sitesQuery.isPending,
    sitesQuery.isError,
    refetchMe,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
